import { fetchStorefront } from '@utils/GraphQLClient';
import { VARIANT_DATA } from '../../ProductCards/Composables/FetchCards';
import { SECONDARY_PRODUCTS_QUERY, PRODUCT_QUERY } from '../../Product/Composables/Queries';
import { formatID } from '@src/js/utils/format';

const VARIANT_QUERY = `query ProductVariant($handle: String!, $options: [SelectedOptionInput!]!) {
  product(handle: $handle) {
    variantBySelectedOptions(selectedOptions: $options) {
        ...variantData
    }
  }
}${VARIANT_DATA}`;

const data = async (handle, options) => {
  return {
    variables: {
      handle: handle,
      options: options,
    },
    query: VARIANT_QUERY,
  };
};

export const fetchSwatch = (color) => {
  const swatches = window?.Brooklinen?.product?.swatches || false;
  const matchingSwatch = swatches.filter((swatch) => swatch.name === color);

  if (matchingSwatch.length <= 0 || !matchingSwatch[0].value) return false;

  const swatch = matchingSwatch[0].value;
  const isHex = swatch[0] === '#';

  const style = isHex ? `background-color: ${swatch}` : swatch;

  return style;
};

export const isHex = (color) => {
  const swatches = window?.Brooklinen?.product?.swatches || false;
  const matchingSwatch = swatches.filter((swatch) => swatch.name === color);

  if (matchingSwatch.length <= 0 || !matchingSwatch[0].value) return false;

  const file = matchingSwatch[0].value;
  return file[0] === '#';
};

export const getOptionsFromWindow = (handle, sku) => {
  const global = window?.Brooklinen?.product?.skus || false;
  if (!global) return;

  const product = global[handle];
  if (!product) return;

  const options = product.skus[sku];

  return options;
};

/**
 * Fetches secondary products based on the provided product handle.
 *
 * @param {string} handle - The product handle used to query secondary products.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of secondary product objects,
 *                                   each containing a handle and color options.
 * @throws Will throw an error if the query fails.
 */
export const getSecondaryProducts = async (handle) => {
  const request = {
    variables: {
      handle,
    },
    query: SECONDARY_PRODUCTS_QUERY,
  };
  try {
    const response = await fetchStorefront(request);
    const { product } = response.data;

    // Extract secondary handles, items merged, and swatch limits
    const secondaryHandles = JSON.parse(product?.handles?.value || '[]');
    const mergedItems = JSON.parse(product?.merged?.value || '[]');
    const itemLimits = JSON.parse(product?.limits?.value || '[]');

    // If the options of a secondary item are to be merged, return them
    if (mergedItems.length > 0) {
      const secondaryItems = secondaryHandles.reduce(async (arr, handle, index) => {
        const mergePermitted = mergedItems[index] ?? false;
        if (!mergePermitted) return arr;

        const { data } = await fetchStorefront({
          variables: { handle },
          query: PRODUCT_QUERY,
        });

        const color = data.product.options.find((option) => option.name === 'Color');
        const limit = itemLimits.length > 0 ? parseInt(itemLimits[index]) : color.values.length;
        color.values = color.values.slice(0, limit);

        arr.push({ handle: handle, swatches: color });
        return arr;
      }, []);

      return secondaryItems;
    }

    // Return an empty array if secondary items are not to be merged
    return [];
  } catch (err) {
    throw new Error(`${handle}'s Secondary Products Query Failed.`, err);
  }
};

export const productVariant = {
  fromSku: (handle, sku) => getOptionsFromWindow(handle, sku),
  fromStorefront: async (handle, options) => {
    if (!options || !handle) return;
    const product = await data(handle, options);

    const response = fetchStorefront(product).then((res) => {
      const data = res?.data?.product?.variantBySelectedOptions ?? false;

      if (data) {
        return data;
      } else {
        return false;
      }
    });

    return response;
  },
};

export const quickAdd = (item) => {
  const promo = window.Brooklinen?.promo?.discounts ?? {};
  const categoryId = promo?.category?.promoName ?? '';

  const variants = item.variants;

  const variant = window.location.pathname.includes('/collections') ? variants[0] : item.selectedVariant;

  const id = formatID(variant.id);
  const comparePrice = variant?.compareAtPrice?.amount;
  const price = variant.price.amount;
  const tags = item.tags;
  const originalPrice = comparePrice > 1 ? comparePrice : price;
  const tprReduction = comparePrice > 1;

  const isCategory = () => tags.some((tag) => tag.includes('Category Promo:'));
  const isLastCall = () => tags.some((tag) => tag.includes('Last Call Assortment'));

  let discount, categoryDiscount, tprDiscount;
  if (tprReduction && !isCategory() && !isLastCall()) {
    tprDiscount = 100 - (price / comparePrice) * 100;
  }

  const categoryPromoName = isCategory() ? categoryId : '';
  if (isCategory()) {
    categoryDiscount = tags
      .find((tag) => tag.includes('Category Promo:'))
      ?.split(':')[1]
      .trim();
  }
  if (isLastCall()) {
    discount = tags
      .find((tag) => tag.includes('Discount Amount:'))
      ?.split(':')[1]
      .trim();
  }

  const createEvent = (items) =>
    new CustomEvent('theme:add-to-cart', {
      detail: items,
    });

  const payload = {
    id: parseInt(id),
    quantity: 1,
    properties: {
      _msrp: originalPrice,
      _category_id: categoryPromoName,
      _category_discount: categoryDiscount,
      _isGiftCard: false,
      Ships: variant?.shipsBy?.value,
      _lc_discount: discount,
      _tpr_discount: tprDiscount,
    },
  };
  const event = createEvent(payload);
  document.dispatchEvent(event);
};
